import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { AuthApiService } from '../../core/services/auth-api.service';
import {
  ApiError,
  ERROR_TYPE,
  FEDERATED_PROVIDER_LABEL,
  LoginDetail,
  LoginRedirectError,
  LoginResponse,
  LoginTotp,
} from '../../core/models/login.model';
import { applyClientStyles } from '../../core/theme/client-theme';
import { Brand } from '../../shared/ui/brand';
import { QrCode } from '../../shared/ui/qr-code';
import { retryAfterText } from '../../shared/ui/retry-after';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, Brand, QrCode],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login implements OnInit {
  private api = inject(AuthApiService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private title = inject(Title);

  /** Presente solo quando si arriva qui da /sso/v1/authorize. Scade in 180s (lato sso-bck). */
  private readonly opaque = this.route.snapshot.queryParamMap.get('opaque');

  protected readonly form = new FormGroup({
    username: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  protected readonly hasAuthorizationRequest = this.opaque !== null;

  /**
   * Il secondo passaggio, quando la password è giusta ma serve anche il codice dell'app di
   * autenticazione. Finché è valorizzato la pagina mostra il codice al posto del form.
   */
  protected readonly totp = signal<LoginTotp | null>(null);
  protected readonly totpCode = new FormControl('', { nonNullable: true });
  protected readonly secretCopied = signal(false);

  /**
   * Il campo del codice prende il focus appena compare: l'attributo `autofocus` vale solo al
   * caricamento della pagina, non per un elemento aggiunto dopo.
   */
  private readonly totpInput = viewChild<ElementRef<HTMLInputElement>>('totpInput');
  private readonly focusTotp = effect(() => this.totpInput()?.nativeElement.focus());

  /** La chiave per l'inserimento a mano, a gruppi di quattro: si ricopia meglio. */
  protected readonly groupedSecret = computed(
    () =>
      this.totp()
        ?.secret?.match(/.{1,4}/g)
        ?.join(' ') ?? '',
  );

  /**
   * I pulsanti dei provider federati.
   *
   * Servono l'opaque — il login federato riprende sempre una richiesta di autorizzazione,
   * sso-bck non lo avvia senza — e il dettaglio del client, che dice quali provider sono
   * attivi. Senza l'uno o l'altro non si mostra nulla e resta il form.
   */
  protected readonly providers = computed(() => {
    const detail = this.client();
    if (this.opaque === null || detail === null) {
      return [];
    }
    return (detail.providers ?? []).map((type) => ({
      type,
      label: FEDERATED_PROVIDER_LABEL[type] ?? `Accedi con ${type}`,
    }));
  });

  /** Partito il redirect verso il provider: i controlli restano bloccati. */
  protected readonly federating = signal<string | null>(null);

  protected readonly submitting = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  /** Navigazione top-level verso l'applicazione in corso: il form resta bloccato. */
  protected readonly redirecting = signal(false);
  /** Login riuscito senza un flusso OIDC da riprendere. */
  protected readonly sessionCreated = signal(false);
  /**
   * La richiesta di autorizzazione non è più utilizzabile (opaque scaduto o già consumato):
   * riprovare da qui non porta a nulla, serve ripartire dall'applicazione.
   */
  protected readonly requestExpired = signal(false);
  /**
   * L'identità esterna non ha un utente sull'SSO e sso-bck ha girato la richiesta di
   * accesso a un amministratore. Il giro finisce qui: finché non viene abilitato non c'è
   * nulla da riprovare, né con il provider né con username e password.
   */
  protected readonly accessRequested = signal(false);

  protected readonly passwordVisible = signal(false);
  /**
   * Bloc Maiusc attivo mentre si digita la password.
   *
   * Il campo è mascherato, quindi l'unico segnale che qualcosa non va sarebbe un
   * "credenziali non valide" dopo l'invio: è una delle cause più banali di accesso
   * fallito, e l'unica che la pagina può riconoscere da sé.
   */
  protected readonly capsLock = signal(false);

  /**
   * Applicativo che ha avviato l'accesso, per personalizzare la pagina.
   *
   * Resta null se la richiesta fallisce — opaque scaduto, servizio non raggiungibile — e in
   * quel caso la pagina mostra la formulazione generica. È una rifinitura: non deve poter
   * impedire di autenticarsi, quindi non blocca il rendering e non produce messaggi d'errore.
   */
  protected readonly client = signal<LoginDetail | null>(null);
  /** Il logo configurato non si carica: si mostra il solo nome invece di un riquadro rotto. */
  protected readonly logoBroken = signal(false);

  /**
   * Il riquadro con logo e nome esiste solo se c'è davvero un'immagine da mostrare.
   *
   * Senza logo si ridurrebbe a un rettangolo bordato contenente del testo, che posato sopra
   * i campi del form si legge come un input disabilitato — ed è esattamente come appariva.
   * Il nome del client non va perso: quando il riquadro non c'è lo porta la frase sotto al
   * titolo, che così resta anche l'unico posto in cui compare (prima era ripetuto in due).
   */
  protected readonly clientBadge = computed(() => {
    const detail = this.client();
    return detail !== null && detail.clientLogoUrl !== null && !this.logoBroken() ? detail : null;
  });

  ngOnInit(): void {
    this.showRedirectError();

    if (this.opaque === null) {
      return;
    }
    this.api
      .loginDetail(this.opaque)
      .pipe(
        tap((detail) => {
          this.client.set(detail);
          applyClientStyles(detail.clientStyles);
        }),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  protected readonly locked = computed(
    () =>
      this.submitting() ||
      this.redirecting() ||
      this.sessionCreated() ||
      this.requestExpired() ||
      this.accessRequested() ||
      this.federating() !== null,
  );

  protected startFederated(type: string): void {
    if (this.locked() || this.opaque === null) {
      return;
    }
    this.errorMessage.set(null);
    this.federating.set(type);
    window.location.assign(this.api.federatedStartUrl(type, this.opaque));
  }

  /**
   * Esito negativo di un login federato, che sso-bck comunica solo così: rimandando qui con
   * `?error=`. Il parametro si toglie subito dall'indirizzo — l'opaque resta — perché
   * ricaricando la pagina l'errore ricomparirebbe per un tentativo ormai passato.
   */
  private showRedirectError(): void {
    const code = this.route.snapshot.queryParamMap.get('error') as LoginRedirectError | null;
    if (code === null) {
      return;
    }

    const query = this.opaque !== null ? `opaque=${encodeURIComponent(this.opaque)}` : '';
    this.location.replaceState(this.location.path().split('?')[0], query);

    // Non è un errore ma un esito: vale con o senza opaque, la richiesta non si riprende.
    if (code === 'access_requested') {
      this.accessRequested.set(true);
      // Il titolo della rotta direbbe "Accedi", che qui non è più un'opzione.
      this.title.setTitle('Richiesta inoltrata');
      return;
    }

    // Senza opaque non c'è più una richiesta da riprendere: l'unica strada è ripartire
    // dall'applicazione. È il caso di una richiesta scaduta durante il giro sul provider,
    // ma anche di uno `state` non valido, che sso-bck non sa ricondurre a nessuna richiesta.
    if (this.opaque === null || code === 'expired_request') {
      this.requestExpired.set(true);
      this.errorMessage.set(
        code === 'invalid_state'
          ? "L'accesso non è stato completato: è scaduto oppure è partito da un'altra finestra. Torna all'applicazione e riprova."
          : "La richiesta di accesso è scaduta. Torna all'applicazione e riprova.",
      );
      return;
    }

    this.errorMessage.set(REDIRECT_ERROR_MESSAGE[code] ?? REDIRECT_ERROR_MESSAGE.server_error);
  }

  protected checkCapsLock(event: KeyboardEvent): void {
    // getModifierState non è affidabile su tutti gli eventi (l'autofill non ne genera
    // affatto): l'avviso compare mentre si digita e non pretende di essere esaustivo.
    this.capsLock.set(event.getModifierState?.('CapsLock') ?? false);
  }

  protected togglePasswordVisibility(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  protected isInvalid(field: 'username' | 'password'): boolean {
    const control = this.form.controls[field];
    return control.touched && control.invalid;
  }

  protected submit(): void {
    if (this.locked()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);

    this.api
      .login({ ...this.form.getRawValue(), opaque: this.opaque })
      .pipe(
        tap((response) => this.handleSuccess(response)),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe();
  }

  /** Solo cifre, al massimo sei: gli spazi e i trattini che alcune app mostrano non contano. */
  protected onTotpInput(): void {
    const digits = this.totpCode.value.replace(/\D/g, '').slice(0, 6);
    if (digits !== this.totpCode.value) {
      this.totpCode.setValue(digits);
    }
    // Sei cifre sono un codice completo: inviarlo subito risparmia un click.
    if (digits.length === 6) {
      this.submitTotp();
    }
  }

  protected submitTotp(): void {
    const totp = this.totp();
    const code = this.totpCode.value;
    if (totp === null || this.locked()) {
      return;
    }
    if (code.length !== 6) {
      this.errorMessage.set('Il codice è di 6 cifre.');
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);

    this.api
      .loginTotp({ challenge: totp.challenge, code })
      .pipe(
        tap((response) => this.handleSuccess(response)),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe();
  }

  /** Torna alla password: il login in sospeso viene semplicemente abbandonato. */
  protected backToPassword(): void {
    this.totp.set(null);
    this.totpCode.reset();
    this.errorMessage.set(null);
  }

  protected async copySecret(): Promise<void> {
    const secret = this.totp()?.secret;
    if (!secret) {
      return;
    }
    try {
      await navigator.clipboard.writeText(secret);
      this.secretCopied.set(true);
      setTimeout(() => this.secretCopied.set(false), 1800);
    } catch {
      // Copia negata dal browser: la chiave resta selezionabile a mano.
    }
  }

  private handleSuccess(response: LoginResponse): void {
    this.form.reset();

    if (response.totp) {
      this.totp.set(response.totp);
      this.totpCode.reset();
      return;
    }
    this.totp.set(null);

    if (response.redirectUri === null) {
      this.sessionCreated.set(true);
      return;
    }

    // Navigazione top-level, non una fetch: il browser deve arrivare davvero
    // all'applicazione perché questa possa consumare il code e creare la sua sessione.
    this.redirecting.set(true);
    window.location.assign(response.redirectUri);
  }

  private handleError(error: HttpErrorResponse): void {
    const errorType = (error.error as ApiError | null)?.errorType;

    if (errorType === ERROR_TYPE.invalidOpaque) {
      // Le credenziali erano valide e la sessione centrale esiste: è la richiesta
      // di autorizzazione a non essere più valida.
      this.requestExpired.set(true);
      this.errorMessage.set(
        "La richiesta di accesso è scaduta. Torna all'applicazione e riprova: non dovrai reinserire le credenziali.",
      );
      return;
    }

    if (error.status === 0) {
      this.errorMessage.set('Impossibile contattare il servizio di autenticazione. Riprova.');
      return;
    }

    if (errorType === ERROR_TYPE.tooManyRequests || error.status === 429) {
      this.errorMessage.set(`Troppi tentativi. Riprova ${retryAfterText(error)}.`);
      return;
    }

    if (errorType === ERROR_TYPE.invalidTotpCode) {
      this.totpCode.reset();
      this.errorMessage.set(
        this.totp()?.step === 'ENROLL'
          ? 'Codice non corretto. Controlla di aver inquadrato il QR qui sopra e riprova.'
          : 'Codice non corretto, riprova.',
      );
      return;
    }

    if (errorType === ERROR_TYPE.invalidTotpChallenge) {
      // Il login in sospeso è scaduto o già concluso altrove: si riparte dalla password.
      this.backToPassword();
      this.errorMessage.set('È passato troppo tempo. Inserisci di nuovo le credenziali.');
      return;
    }

    if (error.status === 401) {
      // Messaggio volutamente generico: quello del backend contiene lo username.
      this.errorMessage.set('Credenziali non valide.');
      return;
    }

    if (errorType === ERROR_TYPE.notActive) {
      this.errorMessage.set('Questo utente non è attivo. Contatta un amministratore.');
      return;
    }

    this.errorMessage.set('Si è verificato un errore inatteso. Riprova.');
  }
}

/** Messaggi per gli esiti negativi del login federato, quando la richiesta è ancora valida. */
const REDIRECT_ERROR_MESSAGE: Record<LoginRedirectError, string> = {
  invalid_request: 'Questo metodo di accesso non è disponibile. Usa username e password.',
  expired_request: "La richiesta di accesso è scaduta. Torna all'applicazione e riprova.",
  invalid_state: "L'accesso non è stato completato. Riprova.",
  upstream_denied:
    "Accesso con l'account esterno annullato. Puoi riprovare o usare username e password.",
  upstream_error:
    "Il provider esterno non ha completato l'accesso. Riprova tra poco o usa username e password.",
  unknown_identity:
    'Il tuo account non è abilitato a questa applicazione. Contatta un amministratore.',
  username_taken:
    'Esiste già un utente con lo stesso nome, non collegato a questo account. Contatta un amministratore.',
  not_active: 'Questo utente non è attivo. Contatta un amministratore.',
  // Gestito a parte da showRedirectError, con una schermata propria: qui solo per completezza.
  access_requested: "Richiesta di accesso inoltrata all'amministratore.",
  server_error: 'Si è verificato un errore inatteso. Riprova.',
};
